import React, { useRef, useEffect, useState } from "react";
import axios from "axios";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Navigation } from "swiper/modules";

import "swiper/css";
import "swiper/css/navigation";

import QuoteIcons from "assets/img/quote.png";

const QuoteIcon = () => (
  <div className="absolute right-4 top-6 flex h-14 w-14 items-center justify-center rounded-lg bg-emerald-50 sm:right-6 sm:top-8 sm:h-20 sm:w-20">
    <img
      src={QuoteIcons}
      alt="Quote Icon"
      className="h-8 w-8 object-contain sm:h-12 sm:w-12"
    />
  </div>
);

const TestimonialCard = ({ testimonial }) => (
  <article className="relative flex min-h-80 flex-col rounded-2xl bg-white p-4 sm:min-h-96 sm:p-6 lg:p-8">
    <QuoteIcon />

    <p className="pr-4 text-lg font-semibold leading-relaxed text-gray-900 sm:text-xl lg:text-2xl">
      &quot;{testimonial.content}&quot;
    </p>

    <div className="mt-auto border-t border-emerald-100 pt-4">
      <div className="flex items-center gap-3 sm:gap-4">
        <img
          src={
            testimonial.avatar_url ||
            "https://ui-avatars.com/api/?name=" +
              encodeURIComponent(testimonial.name || "User") +
              "&background=31D745&color=fff"
          }
          alt={testimonial.name || "User"}
          className="h-14 w-14 flex-none rounded-full border-4 border-emerald-600 object-cover sm:h-16 sm:w-16"
          loading="lazy"
          onError={(e) => {
            e.currentTarget.src =
              "https://ui-avatars.com/api/?name=" +
              encodeURIComponent(testimonial.name || "User") +
              "&background=31D745&color=fff";
          }}
        />

        <div className="min-w-0">
          <h3 className="truncate text-base font-bold text-gray-900 sm:text-lg">
            {testimonial.name}
          </h3>

          <p className="line-clamp-2 text-sm text-gray-600 sm:text-base">
            {testimonial.role || "-"}
          </p>
        </div>
      </div>
    </div>
  </article>
);

const NavigationButton = ({ direction }) => {
  const isNext = direction === "next";

  const ariaLabel = isNext
    ? "Testimoni berikutnya"
    : "Testimoni sebelumnya";

  const buttonClass = isNext
    ? "testimoni-next bg-[#31D745] hover:bg-[#31D745]"
    : "testimoni-prev bg-[#D8EFDC] text-gray-700 hover:bg-[#D8EFDC]";

  return (
    <button
      type="button"
      aria-label={ariaLabel}
      className={`flex h-12 w-12 items-center justify-center rounded-full text-white transition-colors duration-300 sm:h-14 sm:w-14 ${buttonClass}`}
    >
      <svg
        className="h-6 w-6 sm:h-7 sm:w-7"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2.5}
          d={isNext ? "M9 5l7 7-7 7" : "M15 19l-7-7 7-7"}
        />
      </svg>
    </button>
  );
};

const Testimoni = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isVisible, setIsVisible] = useState(false);

  const sectionRef = useRef(null);

  /**
   * Ambil data testimoni dari API
   */
  const fetchTestimonials = async () => {
    try {
      setLoading(true);

      const response = await axios.get(`/api_w/testimonials`, {
        headers: {
          db_name: process.env.REACT_APP_DB_NAME,
        },
      });

      const responseData = response.data;

      let testimonialData = [];

      if (Array.isArray(responseData)) {
        testimonialData = responseData;
      } else if (Array.isArray(responseData?.data)) {
        testimonialData = responseData.data;
      } else if (Array.isArray(responseData?.data?.data)) {
        testimonialData = responseData.data.data;
      }

      setTestimonials(testimonialData);
    } catch (error) {
      console.error("Gagal mengambil data testimoni:", error);
      setTestimonials([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTestimonials();
  }, []);

  /**
   * Intersection Observer untuk animasi section
   */
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      {
        threshold: 0.1,
      }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="bg-black px-4 py-12 sm:py-16 md:py-20 lg:py-24"
    >
      <div
        className={`mx-auto max-w-6xl transition-all duration-700 ${
          isVisible
            ? "translate-y-0 opacity-100"
            : "translate-y-8 opacity-0"
        }`}
      >
        {/* Header */}
        <div className="mb-8 text-center sm:mb-12">
          <h2 className="text-3xl font-extrabold leading-tight text-black sm:text-5xl lg:text-6xl">
            Apa yang Mereka Katakan?
          </h2>

          <p className="mt-2 text-lg font-medium text-gray-400 sm:mt-4 sm:text-xl">
            Mengenai Madrasah Jateng Digital
          </p>
        </div>

        {/* Loading */}
        {loading && (
          <div className="flex min-h-80 items-center justify-center">
            <div className="h-10 w-10 animate-spin rounded-full border-4 border-emerald-500 border-t-transparent" />
          </div>
        )}

        {/* Tidak ada data */}
        {!loading && testimonials.length === 0 && (
          <div className="flex min-h-80 items-center justify-center rounded-2xl bg-white">
            <p className="text-center text-gray-500">
              Belum ada data testimoni.
            </p>
          </div>
        )}

        {/* Swiper */}
        {!loading && testimonials.length > 0 && (
          <>
            <Swiper
              slidesPerView={1}
              spaceBetween={20}
              loop={testimonials.length > 2}
              autoplay={{
                delay: 5000,
                disableOnInteraction: false,
              }}
              navigation={{
                nextEl: ".testimoni-next",
                prevEl: ".testimoni-prev",
              }}
              breakpoints={{
                640: {
                  slidesPerView: 1,
                  spaceBetween: 20,
                },
                1024: {
                  slidesPerView: 2,
                  spaceBetween: 24,
                },
              }}
              modules={[Autoplay, Navigation]}
              className="testimoni-swiper"
            >
              {testimonials.map((testimonial) => (
                <SwiperSlide key={testimonial.id}>
                  <TestimonialCard testimonial={testimonial} />
                </SwiperSlide>
              ))}
            </Swiper>

            {/* Navigation Buttons */}
            <div className="mt-8 flex items-center justify-center gap-4 sm:mt-10">
              <NavigationButton direction="prev" />
              <NavigationButton direction="next" />
            </div>
          </>
        )}
      </div>
    </section>
  );
};

export default Testimoni;